import { createReducer, on } from '@ngrx/store';
import * as SquareCategoryActions from './square-category.actions';
import { SquareCategory } from './square-category.models';

// ----------------------------------------------------------------------
// @ State
// ----------------------------------------------------------------------

export interface SquareCategoriesState {
    squareCategories: SquareCategory[];
    squareCategory: SquareCategory | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial State
// ----------------------------------------------------------------------

export const initialSquareCategoriesState: SquareCategoriesState = {
    squareCategories: [],
    squareCategory: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Helpers
// ----------------------------------------------------------------------

function replaceCategoryBySquareId(
    categories: SquareCategory[],
    squareId: string,
    newData: any
): SquareCategory[] {
    return categories.map((cat) => {
        if (cat.square_id === squareId) return newData;
        if (cat.sub_categories?.length) {
            return {
                ...cat,
                sub_categories: replaceCategoryBySquareId(
                    cat.sub_categories,
                    squareId,
                    newData
                ),
            };
        }
        return cat;
    });
}

function updateCategoryRecursively(
    categories: SquareCategory[],
    data: any
): SquareCategory[] {
    return categories.map((cat: any) => {
        if (cat.square_id === data.square_id) return data;
        if (cat.sub_categories?.length) {
            return {
                ...cat,
                sub_categories: updateCategoryRecursively(
                    cat.sub_categories,
                    data
                ),
            };
        }
        return cat;
    });
}

function deleteCategoryRecursively(
    categories: SquareCategory[],
    squareId: string
): SquareCategory[] {
    return categories
        .filter((cat) => cat.square_id !== squareId)
        .map((cat) => ({
            ...cat,
            sub_categories: cat.sub_categories?.length
                ? deleteCategoryRecursively(cat.sub_categories, squareId)
                : cat.sub_categories,
        }));
}

// ----------------------------------------------------------------------
// @ Reducer
// ----------------------------------------------------------------------

export const squareCategoriesReducer = createReducer(
    initialSquareCategoriesState,

    // Load all
    on(SquareCategoryActions.loadSquareCategories, (state) => ({
        ...state,
        isLoading: true,
        error: null,
    })),
    on(
        SquareCategoryActions.loadSquareCategoriesSuccess,
        (state, { squareCategories }) => ({
            ...state,
            isLoading: false,
            squareCategories,
        })
    ),
    on(
        SquareCategoryActions.loadSquareCategoriesFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // Load single
    on(SquareCategoryActions.loadSquareCategory, (state) => ({
        ...state,
        isLoading: true,
        error: null,
    })),
    on(
        SquareCategoryActions.loadSquareCategorySuccess,
        (state, { squareCategory }) => ({
            ...state,
            isLoading: false,
            squareCategory,
        })
    ),
    on(SquareCategoryActions.loadSquareCategoryFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // Create
    on(SquareCategoryActions.createSquareCategory, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareCategoryActions.createSquareCategorySuccess,
        (state, { data, parentSquareId }) => ({
            ...state,
            isLoading: false,
            squareCategories: parentSquareId
                ? replaceCategoryBySquareId(
                      state.squareCategories,
                      parentSquareId,
                      data
                  )
                : [...(state.squareCategories ?? []), data],
        })
    ),
    on(
        SquareCategoryActions.createSquareCategoryFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // Update
    on(SquareCategoryActions.updateSquareCategory, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareCategoryActions.updateSquareCategorySuccess,
        (state, { data }) => ({
            ...state,
            isLoading: false,
            squareCategories: updateCategoryRecursively(
                state.squareCategories,
                data
            ),
        })
    ),
    on(
        SquareCategoryActions.updateSquareCategoryFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // Delete
    on(SquareCategoryActions.deleteSquareCategory, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareCategoryActions.deleteSquareCategorySuccess,
        (state, { squareId }) => ({
            ...state,
            isLoading: false,
            squareCategories: deleteCategoryRecursively(
                state.squareCategories,
                squareId
            ),
        })
    ),
    on(
        SquareCategoryActions.deleteSquareCategoryFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    )
);
